import React from "react";
import _compact from "lodash/compact";
import _join from "lodash/join";
import { useToggle } from "usehooks-ts";
import {
  checkIsAgent,
  checkIsCD,
  getUserAttribute,
  UserAttributes,
} from "shared-auth";
import { ContactPerformerPopUp } from "component-library";
import { useTranslation } from "i18n";
import { ExternalLinkIcon, PhoneLandlineIcon, LetterCloseIcon } from "assets";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { UserClaimTypes, UserManager } from "core";
import { PROFILE_CONTACT_FULL_PATH, SELF_REP_SL_EMAIL } from "shared/constants";
import useGetReps from "pages/Profile/hooks/useGetReps";
import { SubmitFormEndpoint } from "component-library/src/components/Organisms/ContactPerformerPopUp/PerformerContact";
import { getUrlWithProtocol } from "shared/utils/getUrlWithProtocol";

interface Props {
  isEdit?: boolean;
}

const RepsSection = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const [isContactModalOpen, toggleContactModal] = useToggle();
  const {
    agents = [],
    schools = [],
    isSelfRepresented,
    isLookingForRepresentation,
    isLoading,
  } = useGetReps();

  const isAgent = checkIsAgent();
  const isCd = checkIsCD();

  const seekingRepresentation = isAgent && isLookingForRepresentation;

  const agentLists = [
    ...agents,
    ...schools.map((school) => ({
      ...school,
      name: t("performer:profile.training.title.named", { name: school.name }),
    })),
  ];

  return (
    <ProfileCard
      className="grid grid-cols-1 gap-4 text-s"
      isEdit={!isAgent && isEdit}
      editToPath={PROFILE_CONTACT_FULL_PATH}
      isLoading={isLoading}
    >
      {isSelfRepresented && (
        <div>
          <h2 className="text-s font-semibold leading-5">
            {t("performer:profile.representation.selfRepresented")}
          </h2>
          {seekingRepresentation && (
            <p className="text-s">
              {t("performer:profile.representation.seekingRepresentation")}
            </p>
          )}
          {seekingRepresentation || isCd ? (
            <button
              className="flex items-center gap-2 font-medium text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
              onClick={toggleContactModal}
            >
              <LetterCloseIcon width={13} className="shrink-0" />
              {t("performer:profile.representation.button.contact")}
            </button>
          ) : (
            <a
              href={`mailto:${SELF_REP_SL_EMAIL}`}
              className="mt-1 flex items-center gap-2 font-medium text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
            >
              <LetterCloseIcon width={13} className="shrink-0" />
              {SELF_REP_SL_EMAIL}
            </a>
          )}
        </div>
      )}
      {!isSelfRepresented &&
        agentLists.map((agent) => {
          const address = _join(
            _compact([
              agent.address?.addressLine1,
              agent.address?.addressLine2,
              agent.address?.addressLine3,
              agent.address?.addressLine4,
              agent.address?.country,
              agent.address?.postCode,
            ]),
            ", "
          );

          return (
            <div
              key={agent.refNumber}
              className="border-b border-white/10 pb-1.5 last:border-b-0"
            >
              <a
                className="group flex items-center gap-2 text-color-brand-one hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
                href={getUrlWithProtocol(agent.website || "")}
                target="_blank"
                rel="noreferrer"
              >
                <h2 className="text-s font-semibold leading-4">{agent.name}</h2>
                <ExternalLinkIcon className="invisible group-hover:visible" />
              </a>

              {address && <p className="text-s">{address}</p>}

              {agent.phone && (
                <div className="flex items-center gap-2 dark:text-white/90">
                  <PhoneLandlineIcon className="text-grey-void dark:text-white/90" />
                  <span className="text-s">{agent.phone}</span>
                </div>
              )}

              {agent.email && (
                <a
                  href={`mailto:${agent.email}`}
                  className="flex items-center gap-2 font-medium text-color-brand-one break-word hover:text-color-brand-two dark:text-blue-aquamarine dark:hover:text-white"
                >
                  <LetterCloseIcon width={13} className="shrink-0" />
                  {agent.email}
                </a>
              )}
            </div>
          );
        })}

      <ContactPerformerPopUp
        isOpen={isContactModalOpen}
        closeForm={toggleContactModal}
        performerName={UserManager.getClaimWithDefault(
          UserClaimTypes.profileFullName
        )}
        senderName={getUserAttribute(UserAttributes.FullName)}
        senderEmail={getUserAttribute(UserAttributes.Email)}
        performerId={UserManager.userId}
        canAttachFiles={isCd}
        emailSubjectLine={
          isAgent
            ? t("performer:profile.representation.fallbackEmailSubject")
            : undefined
        }
        submitformEndpoint={
          isAgent
            ? SubmitFormEndpoint.LookingForRep
            : SubmitFormEndpoint.SelfRep
        }
      />
    </ProfileCard>
  );
};

export default RepsSection;
